/**
 * Cash follow-up screen — shown after a guest creates a `cash` split.
 *
 * Polls /v1/online-ordering/payments/orders/{orderId}/splits every 3s and
 * watches for the specific splitId we created. Three states map to UI:
 *   - awaiting_collection → payer name large + short code as fallback line
 *   - paid                → success view + amount
 *   - released / failed   → benign "cancelled" copy + back-to-order CTA
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { TopBar } from "@/components/ui";
import { paymentSplitApi, type PaymentSplit } from "@/api/payment-split";
import { useFormatPrice } from "@/hooks/useFormatPrice";
import { trackSplitPaid } from "@/lib/analytics-events";
import { useAuth } from "@/store/auth";
import { payerForOrder } from "@/lib/payer-token";
import { readParticipant } from "@/lib/participant-token";

const POLL_MS = 3000;

export function CashFollowUpScreen() {
  const { t } = useTranslation();
  const { orderId, splitId } = useParams<{
    orderId: string;
    splitId: string;
  }>();
  const navigate = useNavigate();
  const { format } = useFormatPrice();

  const [split, setSplit] = useState<PaymentSplit | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Name-first cash collection (spec §7): prefer the server-stamped name on
  // the split, then local identity stores, then the signed-in profile.
  const payerName = useMemo(() => {
    return (
      split?.claimedByDisplayName ??
      payerForOrder(orderId)?.displayName ??
      readParticipant()?.displayName ??
      user?.name ??
      null
    );
  }, [split, orderId, user]);

  useEffect(() => {
    if (!orderId || !splitId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      try {
        const res = await paymentSplitApi.list(orderId);
        if (cancelled) return;
        const found = res.splits.find((s) => s.splitId === splitId) ?? null;
        // Detect the awaiting → paid transition once and emit telemetry.
        if (
          found &&
          found.state === "paid" &&
          split?.state !== "paid" &&
          orderId
        ) {
          trackSplitPaid({
            orderId,
            splitId: found.splitId,
            method: found.method,
            amount:
              found.subtotalAmount + found.taxAmount + (found.tipAmount ?? 0),
          });
        }
        setSplit(found);
        if (found && (found.state === "paid" || found.state === "released" || found.state === "failed")) {
          // Stop polling on terminal states.
          return;
        }
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message);
      }
      timer = setTimeout(poll, POLL_MS);
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [orderId, splitId]);

  const owed = useMemo(() => {
    if (!split) return 0;
    return split.subtotalAmount + split.taxAmount;
  }, [split]);

  if (error && !split) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "#b91c1c" }}>
        {error}
      </div>
    );
  }

  if (!split) {
    return (
      <div style={pageStyle}>
        <TopBar
          title={t("v2.split_pay.title")}
          onBack={() => navigate(`/dine-in-order/${orderId}`)}
        />
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: "var(--ink-3)",
            fontSize: 13,
          }}
        >
          {t("v2.split_pay.auto_refresh")}
        </div>
      </div>
    );
  }

  if (split.state === "paid") {
    return (
      <div style={pageStyle}>
        <TopBar
          title={t("v2.split_pay.title")}
          onBack={() => navigate(`/dine-in-order/${orderId}`)}
        />
        <div
          style={{
            padding: "40px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14,
            textAlign: "center",
          }}
        >
          <div className="display"
            aria-hidden
            style={{
              width: 64,
              height: 64,
              borderRadius: 999,
              background: "var(--primary-50)",
              color: "var(--primary-700)",
              display: "grid",
              placeItems: "center",
              fontSize: 30,
              fontWeight: 800,
            }}
          >
            ✓
          </div>
          <div className="display" style={{ fontSize: 22, fontWeight: 800 }}>
            {t("v2.split_pay.squared_up")}
          </div>
          <div style={{ fontSize: 14, color: "var(--ink-3)" }}>
            {t("v2.split_pay.waiter_confirmed")}
          </div>
          <div className="display"
            style={{
              marginTop: 12,
              padding: "10px 18px",
              background: "var(--surface-2)",
              borderRadius: "var(--radius-card)",
              fontSize: 16,
              fontWeight: 800,
            }}
          >
            {format(split.subtotalAmount + split.taxAmount + (split.tipAmount ?? 0))}
          </div>
          <button
            onClick={() => navigate(`/dine-in-order/${orderId}`, { replace: true })}
            style={ctaStyle}
          >
            {t("v2.split_pay.back_to_order")}
          </button>
        </div>
      </div>
    );
  }

  if (split.state === "released" || split.state === "failed") {
    return (
      <div style={pageStyle}>
        <TopBar
          title={t("v2.split_pay.title")}
          onBack={() => navigate(`/dine-in-order/${orderId}`)}
        />
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "var(--ink-3)",
          }}
        >
          <div className="display" style={{ fontSize: 18, fontWeight: 800, color: "var(--ink-1)" }}>
            {t(
              `v2.split_pay.split_state_${split.state}`,
              split.state === "released" ? "Cancelled" : "Failed",
            )}
          </div>
          <button
            onClick={() => navigate(`/dine-in-order/${orderId}`, { replace: true })}
            style={{ ...ctaStyle, marginTop: 18 }}
          >
            {t("v2.split_pay.back_to_order")}
          </button>
        </div>
      </div>
    );
  }

  // Default: awaiting_collection (or transient processing-equivalent).
  const code = split.shortCode ?? "—";

  if (!payerName) {
    // No name anywhere (legacy split from before the display-name rollout):
    // keep the code-first layout so the waiter still has something to match.
    return (
      <div style={pageStyle}>
        <TopBar
          title={t("v2.split_pay.title")}
          onBack={() => navigate(`/dine-in-order/${orderId}`)}
        />
        <div
          style={{
            padding: "32px 20px 80px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 700 }}>
            {t("v2.split_pay.show_code_at_table")}
          </div>
          <div
            className="display"
            style={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 76,
              fontWeight: 900,
              letterSpacing: 12,
              color: "var(--ink-1)",
              background: "var(--surface-2)",
              padding: "20px 24px",
              borderRadius: 18,
              border: "1.5px solid var(--border-strong, #cbd5e1)",
            }}
            data-testid="cash-code"
            data-split-id={split.splitId}
            data-state={split.state}
          >
            {code}
          </div>
          <div
            className="display"
            style={{ marginTop: 6, fontSize: 16, fontWeight: 800, color: "var(--ink-1)" }}
          >
            {format(owed)}
          </div>
          <div style={{ fontSize: 13, color: "var(--ink-3)" }}>
            {t("v2.split_pay.awaiting_waiter")} · {t("v2.split_pay.auto_refresh")}
          </div>
        </div>
      </div>
    );
  }

  // Name-first hierarchy: big name, code demoted to a secondary line.
  return (
    <div style={pageStyle}>
      <TopBar
        title={t("v2.split_pay.title")}
        onBack={() => navigate(`/dine-in-order/${orderId}`)}
      />
      <div
        style={{
          padding: "32px 20px 80px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 14,
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 700 }}>
          {t("v2.split_pay.paying_as")}
        </div>
        <div
          className="display"
          style={{
            fontSize: 44,
            fontWeight: 900,
            letterSpacing: -0.5,
            color: "var(--ink-1)",
            lineHeight: 1.1,
            maxWidth: "100%",
            overflowWrap: "anywhere",
          }}
        >
          {payerName}
        </div>
        <div
          className="display"
          style={{ fontSize: 18, fontWeight: 800, color: "var(--ink-1)" }}
        >
          {format(owed)}
        </div>
        <div
          style={{
            marginTop: 6,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: "var(--surface-2)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: "10px 16px",
            fontSize: 13,
            color: "var(--ink-2)",
            fontWeight: 600,
          }}
        >
          <span>
            {t("v2.split_pay.tell_waiter_name", { name: payerName, code })}
          </span>
        </div>
        <div style={{ fontSize: 13, color: "var(--ink-3)" }}>
          {t("v2.split_pay.awaiting_waiter")} · {t("v2.split_pay.auto_refresh")}
        </div>
      </div>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: "100%",
  background: "var(--surface-1)",
};

const ctaStyle: React.CSSProperties = {
  marginTop: 24,
  padding: "12px 22px",
  background: "var(--ink-1)",
  color: "#fff",
  borderRadius: 12,
  border: "none",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
};
